import React, { useEffect, useState } from 'react';
import { FaBars, FaTimes, FaDownload } from 'react-icons/fa';
import { trackButtonClick, trackSectionView } from '../utils/gtm'; // GTM import
import logo from '../assets/icon.png';
import './Header.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home'); // Track active section

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track active section based on scroll position
  useEffect(() => {
    const sections = ['home', 'features', 'menu', 'process', 'trust', 'download'];
    
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -60% 0px', // Adjust when section becomes "active"
      threshold: 0.1
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id;
          if (sections.includes(sectionId)) {
            setActiveSection(sectionId);
            // GTM: Track section view
            trackSectionView(sectionId.charAt(0).toUpperCase() + sectionId.slice(1));
          }
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Observe all sections
    sections.forEach((sectionId) => {
      const element = document.getElementById(sectionId);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, []);

  // Smooth scroll with header offset
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = 76;
      const elementPosition = element.offsetTop - headerHeight;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth',
      });
    }
    setIsMenuOpen(false);
    // Immediately set active section for better UX
    setActiveSection(sectionId);
  };

  // GTM: Track navigation clicks
  const handleNavClick = (sectionId, location = 'Header') => {
    trackButtonClick(`Navigate to ${sectionId}`, location);
    scrollToSection(sectionId);
  };

  // GTM: Track download button clicks
  const handleDownloadClick = (location = 'Header') => {
    trackButtonClick('Download App', location);
    scrollToSection('download');
  };

  return (
    <header className={`header ${isScrolled ? 'header-scrolled' : ''}`}>
      <div className="container">
        <div className="header-content">
          {/* Logo */}
          <div className="logo" onClick={() => handleNavClick('home')}>
            <div className="logo-icon"> <img src={logo} alt='Mealzy Foods Logo'/></div>
            <div className="logo-text">
              <div className="logo-name">Mealzy</div>
              <div className="logo-subtitle">Foods</div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="nav-desktop">
            <button
              onClick={() => handleNavClick('home')}
              className={`nav-link ${activeSection === 'home' ? 'nav-link-active' : ''}`}
            >
              Home
            </button>
            <button
              onClick={() => handleNavClick('features')}
              className={`nav-link ${activeSection === 'features' ? 'nav-link-active' : ''}`}
            >
              Features
            </button>
            <button
              onClick={() => handleNavClick('menu')}
              className={`nav-link ${activeSection === 'menu' ? 'nav-link-active' : ''}`}
            >
              Menu
            </button>
            <button
              onClick={() => handleNavClick('process')}
              className={`nav-link ${activeSection === 'process' ? 'nav-link-active' : ''}`}
            >
              How it works
            </button>
            <button
              onClick={() => handleNavClick('trust')}
              className={`nav-link ${activeSection === 'trust' ? 'nav-link-active' : ''}`}
            >
              Why us
            </button>
            <button
              onClick={() => handleDownloadClick('Header Desktop')}
              className={`btn btn-primary ${activeSection === 'download' ? 'btn-active' : ''}`}
            >
              <FaDownload /> Download
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="mobile-menu-btn"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        <div className={`mobile-menu ${isMenuOpen ? 'mobile-menu-open' : ''}`}>
          <div className="mobile-menu-content">
            <button
              onClick={() => handleNavClick('home', 'Header Mobile')}
              className={`mobile-nav-link ${activeSection === 'home' ? 'mobile-nav-link-active' : ''}`}
            >
              Home
            </button>
            <button
              onClick={() => handleNavClick('features', 'Header Mobile')}
              className={`mobile-nav-link ${activeSection === 'features' ? 'mobile-nav-link-active' : ''}`}
            >
              Features
            </button>
            <button
              onClick={() => handleNavClick('menu', 'Header Mobile')}
              className={`mobile-nav-link ${activeSection === 'menu' ? 'mobile-nav-link-active' : ''}`}
            >
              Menu
            </button>
            <button
              onClick={() => handleNavClick('process', 'Header Mobile')}
              className={`mobile-nav-link ${activeSection === 'process' ? 'mobile-nav-link-active' : ''}`}
            >
              How it works
            </button>
            <button
              onClick={() => handleNavClick('trust', 'Header Mobile')}
              className={`mobile-nav-link ${activeSection === 'trust' ? 'mobile-nav-link-active' : ''}`}
            >
              Why us
            </button>
            <button
              onClick={() => handleDownloadClick('Header Mobile')}
              className={`btn btn-primary mobile-download-btn ${activeSection === 'download' ? 'btn-active' : ''}`}
            >
              <FaDownload /> Download
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
